import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './Login.module.css';

interface LocationState {
  returnUrl?: string;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, loading, error, clearError } = useAuth();
  const [isInitiating, setIsInitiating] = useState(false);

  // Form states (unused for actual local auth, just for UI realism)
  const [username, setUsername] = useState();
  //const [password, setPassword] = useState('................');
  const [stayLoggedIn, setStayLoggedIn] = useState(true);

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      if (isAuthenticated) {
        const state = location.state as LocationState;
        const params = new URLSearchParams(location.search);
        const returnUrl = state?.returnUrl || params.get('returnUrl') || '/';
        navigate(returnUrl, { replace: true });
        return;
      }

      if (!loading) {
        try {
          const { authService } = await import('../services/authService');
          const authStatus = await authService.getAuthStatus();

          if (authStatus.isAuthenticated) {
            const state = location.state as LocationState;
            const params = new URLSearchParams(location.search);
            const returnUrl = state?.returnUrl || params.get('returnUrl') || '/';
            navigate(returnUrl, { replace: true });
          }
        } catch (error) {
          console.error('[Login] Error checking auth status:', error);
        }
      }
    };

    checkAuthAndRedirect();
  }, [isAuthenticated, loading, navigate, location.state, location.search]);

  const getReturnUrl = (): string => {
    const state = location.state as LocationState;
    if (state?.returnUrl) {
      return state.returnUrl;
    }

    const params = new URLSearchParams(location.search);
    return params.get('returnUrl') || '/';
  };

  const handleSSOLogin = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isInitiating) {
      return;
    }

    setIsInitiating(true);

    try {
      const returnUrl = getReturnUrl();
      await login(returnUrl);
    } catch (error) {
      console.error('[Login] Error initiating SSO login:', error);
      setIsInitiating(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loadingSpinner}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginContainer}>
      {/* Background gradients matching image */}
      <div className={styles.bgGradientLeft}></div>
      <div className={styles.bgGradientRight}></div>

      {/* Top Header */}
      <div className={styles.topHeader}>
        <div className={styles.headerItem}>
          English
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <div className={styles.headerItem}>Help</div>
      </div>

      <div className={styles.loginBoxWrapper}>
        <div className={styles.loginBox}>

          {/* Logo Section */}
          <div className={styles.logoSection}>
            <div className={styles.logoIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <div className={styles.logoText}>Commvault<span style={{ fontSize: '0.4em', verticalAlign: 'super' }}>®</span></div>
            <div className={styles.logoSubtitle}>Customer Support Center</div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.alertError}>
              <span className={styles.alertIcon}>⚠️</span>
              <div>
                <strong>Login Error</strong>
                <p>{error}</p>
              </div>
              <button onClick={clearError} className={styles.closeBtn} aria-label="Close error">✕</button>
            </div>
          )}

          {/* Form */}
          <form className={styles.loginForm} onSubmit={handleSSOLogin}>

            <div className={styles.formGroup}>
              <label>Username or email</label>
              <input
                type="text"
                value={username}
                //onChange={(e) => setUsername(e.target.value)}
                className={styles.inputField}
              />
            </div>

            <div className={styles.formActions}>
              <div className={styles.leftActions}>
                <a href="#" className={styles.forgotLink}>Forgot password?</a>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={stayLoggedIn}
                    onChange={(e) => setStayLoggedIn(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Stay logged in</span>
                </label>
              </div>
              <button
                type="submit"
                disabled={isInitiating}
                className={styles.loginButton}
              >
                {isInitiating ? 'Redirecting...' : 'Login'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
